import React, { useEffect, useMemo, useRef, useState } from 'react'

// TODO: eliminate imports from "old" code paths (components/..., lib/...}, move things to V2
// V1 imports
import { Alert, Card, CardContent, CardHeader, LinearProgress, Typography } from '@barracuda-internal/bds-core'
import { useAppSelector } from 'redux/toolkit/hooks'
import { useFormatMessage } from 'lib/localization'
import styles from 'components/pages/settings/quarantineNotification/QuarantineNotificationStyles'
import appConfig from 'config/appConfig'

// V2 module imports
import { Auth, EgdApiClient, IAuthPolicy, UrlUtils, useI18n } from 'v2/modules/lib'
import { QuarantineNotificationForm } from './quarantineNotificationForm'
import { QuarantineNotificationPage, QuarantineNotificationPageProps } from './QuarantineNotificationPage'

type UIStatePending = { state: 'pending' }
type UIStateReady = { state: 'ready'; props: QuarantineNotificationPageProps }
type UIStateError = { state: 'error'; error: unknown }

type UIState = UIStatePending | UIStateReady | UIStateError

const BASE_I18N_KEY = 'ess.settings.quarantine_notification'

export const QuarantineNotificationPageLoader: React.FC = () => {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const classes = styles()

  const { t } = useI18n()
  const canSetState = useRef(true)
  const [uiState, setUiState] = useState<UIState>({ state: 'pending' })

  // TODO: switch to load dependencies via v2 API
  const { accessToken, accessTokenObject } = useAppSelector(_store => ({
    accessToken: _store.auth.accessToken,
    accessTokenObject: _store.auth.accessTokenObject
  }))

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false
    }
  }, [])

  useEffect(() => {
    if (typeof accessToken === 'undefined' || typeof accessTokenObject === 'undefined') {
      // Should not happen, needed here as type guard on redux values
      setUiState({
        state: 'error',
        error: 'unauthenticated'
      })
      return
    }
    // eslint-disable-next-line @typescript-eslint/no-extra-semi
    ;(async () => {
      try {
        const { userId, accountId, domainId } = accessTokenObject
        const urlUtils = UrlUtils()
        const egdApiClient = EgdApiClient({
          host: `${appConfig.GATEWAY_URL}/v2`,
          commonHeaders: {
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined
          },
          urlUtils
        })
        const availableAuthPolicies = await egdApiClient.authService.getAvailableAuthPolicies()
        const auth = Auth(availableAuthPolicies as IAuthPolicy[])
        const settingsForm = QuarantineNotificationForm({
          egdApiClient,
          accountId,
          domainId,
          userId,
          auth
        })
        if (!canSetState.current) {
          return
        }
        if (!settingsForm.userSettingsForm.canRead || !settingsForm.domainSettingsForm.canRead) {
          setUiState({
            state: 'error',
            error: 'unauthorized'
          })
          return
        }
        setUiState({
          state: 'ready',
          props: {
            settingsForm
          }
        })
      } catch {
        if (!canSetState.current) {
          return
        }
        setUiState({
          state: 'error',
          error: 'failed_to_load_dependencies'
        })
      }
    })()
  }, [accessToken, accessTokenObject])

  // TODO: when switching the UI to BDS v2 improve/standardize the UI state loading phases
  return useMemo(() => {
    switch (uiState.state) {
      case 'ready':
        return <QuarantineNotificationPage {...uiState.props} />
      case 'error':
        return (
          <Card elevation={1} data-testid="root-card">
            <CardHeader
              title={
                <Typography className={classes.title} data-testid="title">
                  {formatMessage('title')}
                </Typography>
              }
            />
            <CardContent>
              <Alert severity="error" style={{ width: '100%' }}>
                {t(`page_loader_errors.${uiState.error}`)}
              </Alert>
            </CardContent>
          </Card>
        )
      case 'pending':
      default:
        return (
          <Card elevation={1} data-testid="root-card">
            <CardHeader
              title={
                <Typography className={classes.title} data-testid="title">
                  {formatMessage('title')}
                </Typography>
              }
            />
            <CardContent>
              <LinearProgress />
            </CardContent>
          </Card>
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState.state])
}
